import { ContactEmail, ContactTextMessage } from "../../../../components/Icons/Icons"
import { SendEmail } from "./SendEmail"
import { SendText } from "./SendText"
import React, { useEffect, useState, useRef } from "react"
import { ReviewPlanDetails } from "./ReviewPlanDetails"
import { EnrolledDebtsCard } from "./EnrolledDebtsCard"
import done from '../../../../assets/images/icons/done.svg'
import { getContactById, getQCCallConfig, editContactById } from "../../../../features/customer/customerSlice"
import { getLsaTypeDoc } from "../../../../features/documents/documents"
import { useDispatch } from "react-redux"
import PaymentModal from "./PaymentModal"
import CardSplit from "./CardSplit"
import ToolTip from "../../../../components/ToolTip/ToolTip"
import { formatFullDate } from "../../../../utils/date"
import UploadLSADocumentModal from "./UploadLSA"
import { StorageGetItem, USER } from "../../../../lib/storage"
import PdfViewModal from "../../../../components/PdfViewModal/PdfViewModal"
import { ProposalButton } from "./ProposalButton"
import AffiliateBilling from './AffiliateBilling'
import { showSnackBar } from "../../../../components/Snackbar/Snackbar"
import Switch from "../../../../components/Switch/Switch"
import { useMyContextLoading } from "../../../../components/LoadingContextProvider/LoadingContextProvider"
import ContractSignedModal from "../../../../components/ContractSignedModal/ContractSignedModal"
import { isCreditSelectionPersisted } from "../helper"
import LsaChargeNamePicker from "../../../../components/LsaChargeName/LsaChargeNamePicker"
import { CHARGE_NAME_NOT_REQUIRED, PICK_CHARGE_NAME_HINT } from "../../../../constants/lsaChargeName"

const LSA_TYPE_DOC_BY_LSA_TYPE = {
  english: 'legalServiceAgreement',
  bilingual: 'legalServiceAgreementSpanish',
}


export const Review = ({
  paymentSchedule,
  programPlan,
  userId,
  basicInfoEmail,
  emailFromBank, primaryPhone,
  confirmEmail,
  setConfirmEmail,
  isBillingAccount,
  checkUserBankAccountAndSigningInfo,
  isSignedMainDoc,
  paymentMethod,
  userInfo,
  selectedCreditLines,
  savedCreditProfileIds,
  skipQCCall,
  setSkipQCCall,
  coApplicantId,
  coApplicantInfo,
  contactGroupConfig,
  emailOptional = false,
  debtSummary = false,
  chargeName = CHARGE_NAME_NOT_REQUIRED,
  stopOnSendError = false,
}) => {
  const dispatch = useDispatch()
  const { triggerLoading } = useMyContextLoading()

  // WS #2219705: a PACK client with no email can only get the link by text, so the step opens there until
  // the rep picks a tab. Everyone else opens on email, as before.
  const [pickedReview, setSelectedReview] = useState(null)
  const selectedReview = pickedReview || (emailOptional && !basicInfoEmail ? "send-text" : "send-email")
  const [userData, setUserData] = useState("")
  const [triggerMethodAdded, setTriggerMethodAdded] = useState("")
  const [userRole, setUserRole] = useState('')
  const [qcCallConfig, setQcCallConfig] = useState(null)
  const [showSignedModal, setShowSignedModal] = useState(false)
  const [lsaTemplateType, setLsaTemplateType] = useState('english')
  const prevSignedRef = useRef(isSignedMainDoc)
  const acceptedPaymentMethods = contactGroupConfig?.acceptedPaymentMethods

  // WS #2228900. The server says whether this client's office may split the fee across cards (PACK);
  // the split is for a one-time flat fee only. It replaces the Payment Method button, whose card
  // flow (removeOld) would delete the cards of the split.
  const showCardSplit = !!contactGroupConfig?.feeShape?.cardSplit
    && programPlan?.feeType === 'flat'
    && Number(programPlan?.plan) === 1
    && Number(programPlan?.flatFee) > 0

  // gate on the PERSISTED selection and refuse while it diverges from what the rep
  // currently has ticked: the LSA renders from the database, and mirroring the backend
  // assertLeadSignable guard (see specs/block-lsa-sign-without-debts)
  const signingBlocked = !isCreditSelectionPersisted({ selectedCreditLines, savedCreditProfileIds })
  const chargeNameBlocked = !!chargeName.blocked
  const sendBlocked = signingBlocked || chargeNameBlocked
  const blockedHint = chargeNameBlocked && !signingBlocked ? PICK_CHARGE_NAME_HINT : undefined

  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
    })
  }, [])

  const renderTabContent = () => {
    switch (selectedReview) {
      case "send-email":
        return <div className="m-6">
          <SendEmail
            userId={userId}
            isBillingAccount={isBillingAccount}
            isSignedMainDoc={isSignedMainDoc}
            confirmEmail={confirmEmail}
            setConfirmEmail={setConfirmEmail}
            userData={userData}
            userRole={userRole}
            lsaTemplateType={lsaTemplateType}
            signingBlocked={sendBlocked}
            blockedHint={blockedHint}
            acceptedPaymentMethods={acceptedPaymentMethods}
            stopOnSendError={stopOnSendError}
          />
        </div>
      case "send-text":
        return <div className="m-6">
          <SendText
            userId={userId}
            isBillingAccount={isBillingAccount}
            isSignedMainDoc={isSignedMainDoc}
            primaryPhone={primaryPhone}
            userData={userData}
            userRole={userRole}
            lsaTemplateType={lsaTemplateType}
            signingBlocked={sendBlocked}
            blockedHint={blockedHint}
            stopOnSendError={stopOnSendError}
            acceptedPaymentMethods={acceptedPaymentMethods}
          />
        </div>
    }
  }

  useEffect(() => {
    const intervalId = setInterval(async () => {
      const dataReceived = await checkUserBankAccountAndSigningInfo({ custUserId: userId })
      if (dataReceived) {
        clearInterval(intervalId)
      }
    }, 5000)

    return () => clearInterval(intervalId)
  }, [])

  useEffect(() => {
    checkUserBankAccountAndSigningInfo({ custUserId: userId })
  }, [triggerMethodAdded])

  useEffect(() => {
    if (!prevSignedRef.current && isSignedMainDoc) {
      setShowSignedModal(true)
    }
    prevSignedRef.current = isSignedMainDoc
  }, [isSignedMainDoc])

  const fetchData = async () => {
    try {
      const resultAction = await dispatch(getContactById(userId))
      if (getContactById.fulfilled.match(resultAction)) {
        setUserData(resultAction.payload.data)
      }
    } catch (error) {
      showSnackBar("error", "Failed to fetch contact data")
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    const fetchInitialLsaType = async () => {
      if (!userId) return
      const result = await dispatch(getLsaTypeDoc({ userId }))
      if (getLsaTypeDoc.fulfilled.match(result)) {
        const resolved = result.payload?.data?.lsaType
        if (resolved === 'english' || resolved === 'bilingual') {
          setLsaTemplateType(resolved)
        }
      }
    }
    fetchInitialLsaType()
  }, [userId, dispatch])

  useEffect(() => {
    const fetchQCConfig = async () => {
      if (userId) {
        try {
          const response = await dispatch(getQCCallConfig(userId))
          if (getQCCallConfig.fulfilled.match(response)) {
            setQcCallConfig(response.payload.data)
          }
        } catch (error) {
          showSnackBar("error", `Failed to fetch QC config`)
        }
      }
    }

    fetchQCConfig()
  }, [userId, dispatch])

  const handleQCStatusChange = async (newSkipValue) => {
    if (!userId) return

    triggerLoading(true)
    try {
      const newQcCallStatus = newSkipValue ? 'skipped' : 'pending'

      const response = await dispatch(editContactById({
        userId,
        userData: {
          qcCallStatus: newQcCallStatus
        }
      }))

      if (editContactById.fulfilled.match(response)) {
        setSkipQCCall(newSkipValue)
        showSnackBar("success", `QC Call status updated to: ${newQcCallStatus === 'skipped' ? 'Skipped' : 'Pending'}`)
      } else {
        showSnackBar("error", "Failed to update QC Call status")
      }
    } catch (error) {
      showSnackBar("error", "Failed to update QC Call status")
    } finally {
      triggerLoading(false)
    }
  }

  return (
    <>
      <div className="bg-surface border border-divider shadow-lg rounded-2xl w-full max-w-[798px]">
        <div className="flex justify-between py-6 px-12">
          <div className="flex flex-col">
            <div className="flex items-center">
              <span className="font-bold text-xl-20">Billing Info</span>
              {isBillingAccount && <img src={done} alt="icon" width="24px" />}
              {isSignedMainDoc && <img src={done} alt="icon" width="24px" />}
            </div>
            {isBillingAccount && <div className="flex items-center">
              {paymentMethod?.billingAccountType} -
              ***{paymentMethod?.accountNumberLast4 || paymentMethod?.cardNumberLast4}
              <ToolTip>
                <div className="flex flex-col">
                  <div><span className="font-bold">Type - </span>{paymentMethod?.billingAccountType}</div>
                  {paymentMethod?.billingAccountType === 'Bank Account' ? <div>
                    <span className="font-bold">Account Number - </span>***{paymentMethod?.accountNumberLast4}
                    <div><span className="font-bold">Bank Name - </span>{paymentMethod?.bankName}</div>
                    <div><span className="font-bold">Name On Account - </span>{paymentMethod?.nameOnAccount}</div>
                  </div>
                    :
                    <div>
                      <div><span className="font-bold">Provider - </span>{paymentMethod?.provider}</div>
                      <span className="font-bold">Account Number - </span>***{paymentMethod?.cardNumberLast4}
                      <div><span className="font-bold">Name On Card - </span>{paymentMethod?.nameOnCard}</div>
                    </div>
                  }
                  <div><span className="font-bold">Created At - </span>{formatFullDate(paymentMethod?.createdAt)}</div>
                </div>
              </ToolTip>
            </div>}
          </div>
          <div className="flex flex-row">
            <div className="mr-4">
              <ProposalButton
                userId={userId}
                programPlan={programPlan}
                paymentSchedule={paymentSchedule}
                userInfo={userInfo}
                selectedCreditLines={selectedCreditLines}
              />
            </div>
            {!showCardSplit && <div>
              <PaymentModal
                userId={userId}
                userData={userData}
                isBillingAccount={isBillingAccount}
                removeOld={true}
                setTriggerMethodAdded={setTriggerMethodAdded}
                acceptedPaymentMethods={acceptedPaymentMethods}
              />
            </div>}
          </div>
        </div>
        {showCardSplit && (
          <CardSplit
            userId={userId}
            flatFee={Number(programPlan.flatFee)}
            userData={userData}
            acceptedPaymentMethods={acceptedPaymentMethods}
            onSaved={() => checkUserBankAccountAndSigningInfo({ custUserId: userId })}
          />
        )}
        {qcCallConfig?.enabled && (userRole === 'customer' || userRole === 'user') && (
          <>
            <div className="border-b border-divider mx-6" />
            <div className="py-6 px-12">
              <Switch
                id="skipQCCall"
                checked={skipQCCall}
                onChange={handleQCStatusChange}
                disabled={!userId}
                label="Skip Quality Control Call for this customer"
                size="md"
              />
            </div>
            <div className="border-b border-divider mx-6" />
          </>
        )}
        <div className="my-6 mx-12">
          <div className="flex gap-4 bg-surface rounded-t-2xl items-center justify-between">
            <div className="flex flex-row items-center">
              <div
                className={`flex py-3 ml-6 gap-2 cursor-pointer ${selectedReview === "send-email" ? "border-b-[3px] border-primary-main" : ""}`}
                onClick={() => setSelectedReview("send-email")}
              >
                <div>
                  <ContactEmail fill={`${selectedReview === "send-email" ? "var(--color-blue-700)" : ""}`} />
                </div>
                <div
                  className={`text-md text-text-secondary font-normal ${selectedReview === "send-email" ? "text-text-primary font-semibold" : ""}`}>
                  Send email
                </div>
              </div>
              <div
                className={`flex py-3 ml-6 gap-2 cursor-pointer ${selectedReview === "send-text" ? "border-b-[3px] border-primary-main" : ""}`}
                onClick={() => setSelectedReview("send-text")}
              >
                <div>
                  <ContactTextMessage fill={`${selectedReview === "send-text" ? "var(--color-blue-700)" : ""}`} />
                </div>
                <div
                  className={`text-md text-text-secondary font-normal ${selectedReview === "send-text" ? "text-text-primary font-semibold" : ""}`}>
                  Send text message
                </div>
              </div>
            </div>
            <div className="flex flex-row items-center">
              <div className="mr-4 flex items-center gap-2">
                <span className="text-sm font-medium text-text-primary">LSA:</span>
                <div className="inline-flex rounded-lg border border-divider overflow-hidden">
                  <button
                    type="button"
                    className={`px-4 py-1.5 text-sm font-medium transition-colors ${lsaTemplateType === 'english' ? 'bg-primary-main text-primary-contrast' : 'bg-transparent text-text-secondary hover:text-text-primary'}`}
                    onClick={() => setLsaTemplateType('english')}
                  >
                    English
                  </button>
                  <button
                    type="button"
                    className={`px-4 py-1.5 text-sm font-medium transition-colors ${lsaTemplateType === 'bilingual' ? 'bg-primary-main text-primary-contrast' : 'bg-transparent text-text-secondary hover:text-text-primary'}`}
                    onClick={() => setLsaTemplateType('bilingual')}
                  >
                    Bilingual
                  </button>
                </div>
              </div>
              {chargeName.required && <div className="mr-4">
                <LsaChargeNamePicker userId={userId} />
              </div>}
              {isBillingAccount && !chargeNameBlocked && <div className="mr-4">
                <PdfViewModal
                  iconType="pdf"
                  isLSA={true}
                  billingAccountId={paymentMethod?._id}
                  userId={userId}
                  lsaTypeDoc={LSA_TYPE_DOC_BY_LSA_TYPE[lsaTemplateType]}
                />
              </div>
              }
              {
                (userRole === 'customer' || userRole === 'user') && isBillingAccount && <div className="mr-4">
                  <UploadLSADocumentModal userId={userId}
                    isSignedMainDoc={isSignedMainDoc}
                    checkUserBankAccountAndSigningInfo={checkUserBankAccountAndSigningInfo}
                    signingBlocked={signingBlocked} />
                </div>
              }
            </div>
          </div>
          {/* The affiliate-billed enrolment's one extra step, placed where the rep is about to
              send: without a payment method the LSA controls above are hidden and "Send email"
              offers the old add-your-bank-account link instead of the agreement. */}
          <AffiliateBilling
            userId={userId}
            affiliateBilled={contactGroupConfig?.affiliateBilled}
            hasBilling={isBillingAccount}
            onAdded={() => checkUserBankAccountAndSigningInfo({ custUserId: userId })}
          />
          <div className="border border-divider max-w-[802px] rounded-b-2xl">
            {renderTabContent()}
          </div>
        </div>
      </div>
      {coApplicantId && (
        <div className="bg-surface border border-divider shadow-lg rounded-2xl w-full max-w-[798px] mt-6">
          <div className="py-6 px-12">
            <div className="font-bold text-xl-20 mb-2">Co-Applicant POA</div>
            <div className="text-text-secondary text-sm">
              {coApplicantInfo?.firstName} {coApplicantInfo?.lastName}
            </div>
            <div className="text-text-secondary text-sm mt-1">
              POA document will be generated and sent to co-applicant for signing.
              Enrollment is not blocked by co-applicant POA status.
            </div>
          </div>
        </div>
      )}
      {debtSummary && (
        <EnrolledDebtsCard
          selectedCreditLines={selectedCreditLines}
          isSignedMainDoc={isSignedMainDoc}
          coApplicantId={coApplicantId}
        />
      )}
      <ReviewPlanDetails
        paymentSchedule={paymentSchedule}
        programPlan={programPlan}
      />
      <ContractSignedModal
        open={showSignedModal}
        onClose={() => setShowSignedModal(false)}
      />
    </>
  )
}
